// Listens to V1's AgentForge WebSocket for one session and writes what it
// hears into the store, so the chat surface can render the assistant's
// reply as it streams in.
//
// V1 owns its own socket (`useAgentForgeWS`) — it doesn't share legacy's
// `useMissionControlWS`. Mount this hook exactly once (in the `AgentForge`
// umbrella) so each frame is processed a single time.
//
// Lifecycle mirrors legacy `/agentforge` ChatPanel:
//   • `stream_chunk` → append to the `streamingDraft` accumulator (live UX).
//   • `tool_start` / `tool_result` / `action_progress` → sub-step rows.
//   • `stream_done` / `agent_message` → clear draft, flip tab to `done`,
//     and invalidate the messages query so TanStack refetches the
//     persisted record from the server.
//   • `stream_error` → same teardown plus a console log.

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { messagesKey } from '../constants';
import {
  structuredDataToBlock,
  toCandidateProfilesBlock,
  wantsFullProfiles,
} from '../helpers';
import { useAgentForgeStore } from '../stores';
import { useAgentForgeWS, type AgentForgeWSMessage } from './useAgentForgeWS';

// Read a field as a string, falling back to `fallback` (default '') when
// missing or wrong type. Keeps the event handlers below short.
const asString = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback;

const asNumber = (value: unknown): number | undefined =>
  typeof value === 'number' ? value : undefined;

const TOOL_NAME_FALLBACK = 'Running tool';
const STREAM_ERROR_FALLBACK =
  'Something went wrong while generating a response.';

// Action-progress events use a `status` string; map it to our 3-state union.
const mapActionStatus = (raw: string): 'active' | 'done' | 'error' => {
  if (raw === 'done' || raw === 'completed') return 'done';
  if (raw === 'failed' || raw === 'error') return 'error';
  return 'active';
};

export const useAgentForgeStream = (
  sessionId: string | null | undefined
): { connected: boolean } => {
  const { connected, subscribe, reconnectGeneration } = useAgentForgeWS();
  const queryClient = useQueryClient();

  // Backfill on every reconnect. Even with the server-side Redis Streams
  // replay there are gaps the stream can't cover (events older than the
  // replay cap, or the very first event after a long-disconnected
  // session). Invalidating the messages query is cheap and idempotent —
  // TanStack refetches the canonical transcript from the server and
  // reconciles. Skip on generation 0 (the very first connect — nothing
  // to backfill yet, the page-mount fetch already ran).
  useEffect(() => {
    if (!sessionId || reconnectGeneration === 0) return;
    queryClient.invalidateQueries({ queryKey: messagesKey(sessionId) });
  }, [reconnectGeneration, sessionId, queryClient]);

  const appendStreamDelta = useAgentForgeStore((s) => s.appendStreamDelta);
  const pushSubStep = useAgentForgeStore((s) => s.pushSubStep);
  const patchSubStep = useAgentForgeStore((s) => s.patchSubStep);
  const pushOutputBlock = useAgentForgeStore((s) => s.pushOutputBlock);
  const patchBlockChips = useAgentForgeStore((s) => s.patchBlockChips);
  const clearStreamingDraft = useAgentForgeStore((s) => s.clearStreamingDraft);
  const commitStreamingDraft = useAgentForgeStore(
    (s) => s.commitStreamingDraft
  );
  const setTabStatus = useAgentForgeStore((s) => s.setTabStatus);

  useEffect(() => {
    if (!sessionId) return undefined;

    // At run-end commit the WS draft into a permanent assistant message AND
    // invalidate the server transcript query so `useAgentForgeMessages`
    // refetches with the now-complete record (including the final assistant
    // text saved by the worker after stream_done). The hydrate hook skips
    // mid-stream, so the refetch only takes effect now that streaming is
    // flagged done.
    const commitFromServer = () => {
      commitStreamingDraft();
      setTabStatus(sessionId, 'done');
      queryClient.invalidateQueries({ queryKey: messagesKey(sessionId) });
    };

    type Handler = (_payload: Record<string, unknown>) => void;
    const handlers: Record<string, Handler> = {
      // Append a token to the live assistant draft.
      stream_chunk: (p) => {
        const delta = asString(p.delta);
        if (delta) appendStreamDelta(sessionId, delta);
      },

      // A tool just started running. Add it as an "active" sub-step row.
      tool_start: (p) => {
        pushSubStep(sessionId, {
          id: asString(p.tool_call_id, `tool-${Date.now()}`),
          name: asString(p.tool_name, TOOL_NAME_FALLBACK),
          description: asString(p.summary) || undefined,
          status: 'active',
        });
      },

      // A tool finished. Flip its row to "done" or "error", and — if the
      // backend shipped a structured_data payload — turn it into a rich
      // BlockOutput (candidate list, action result, etc.) and push it onto
      // the streaming draft so the renderer picks it up alongside the text.
      tool_result: (p) => {
        const id = asString(p.tool_call_id);
        if (!id) return;
        const hasError = asString(p.error).length > 0;
        patchSubStep(sessionId, id, {
          status: hasError ? 'error' : 'done',
          description: asString(p.summary) || undefined,
          durationMs: asNumber(p.duration_ms),
        });
        if (!hasError) {
          let block = structuredDataToBlock(p.structured_data, id);
          // INTERIM: promote to the full-profile carousel when asked.
          if (block?.type === 'candidate') {
            const msgs =
              useAgentForgeStore.getState().messagesBySession[sessionId] ?? [];
            const lastUser = [...msgs].reverse().find((m) => m.role === 'user');
            if (wantsFullProfiles(lastUser?.content)) {
              block = toCandidateProfilesBlock(block);
            }
          }
          if (block) pushOutputBlock(sessionId, block);
        }
      },

      // Backend produced LLM-tailored follow-up chips ~500ms after a card
      // first rendered. Swap the static chips for the smart ones in place.
      block_chips_update: (p) => {
        const id = asString(p.tool_call_id);
        if (!id) return;
        const rawChips = Array.isArray(p.chips) ? p.chips : [];
        const chips = rawChips
          .map((c, i) => {
            const obj = c as {
              label?: unknown;
              prompt?: unknown;
              kind?: unknown;
            };
            const label = asString(obj.label);
            const prompt = asString(obj.prompt);
            if (!label || !prompt) return null;
            const kind = obj.kind === 'primary' ? 'primary' : 'secondary';
            return {
              id: `chip-${id}-${i}`,
              label,
              promptTemplate: prompt,
              kind: kind as 'primary' | 'secondary',
            };
          })
          .filter((c): c is NonNullable<typeof c> => c !== null);
        if (chips.length === 0) return;
        patchBlockChips(sessionId, id, chips);
      },

      // Non-tool timeline step (routing, context-load, LLM composing, etc).
      // Keyed by `step` name with an `action:` prefix so action rows and
      // tool rows can coexist in the same `subSteps` list. The backend
      // ships an optional `label` (human-readable) and `duration_ms` —
      // when present the row reads "Loading conversation context · 320 ms"
      // rather than "context".
      action_progress: (p) => {
        const step = asString(p.step);
        if (!step) return;
        const status = mapActionStatus(asString(p.status, 'active'));
        const label = asString(p.label);
        const durationMs = asNumber(p.duration_ms);
        // pushSubStep dedups by id and merges status/description/duration,
        // so emitting `active` then `done` for the same step id updates
        // the same row instead of creating a second one.
        pushSubStep(sessionId, {
          id: `action:${step}`,
          name: label || step,
          description: label && label !== step ? undefined : undefined,
          status,
          ...(typeof durationMs === 'number' ? { durationMs } : {}),
        });
      },

      // Run is complete — the server has the final message. Replace our
      // accumulator with it (handles tool-loop turns where intermediate
      // chunks would otherwise show as garbled text).
      stream_done: commitFromServer,
      agent_message: commitFromServer,

      // Same as done, plus a console error so dev tools surface it. Drop
      // the draft, flip status, and invalidate so any partial state gets
      // refetched from the canonical record.
      stream_error: (p) => {
        console.error(
          'agentforge stream error:',
          asString(p.error) || STREAM_ERROR_FALLBACK
        );
        clearStreamingDraft();
        setTabStatus(sessionId, 'error');
        queryClient.invalidateQueries({ queryKey: messagesKey(sessionId) });
      },
    };

    const unsubscribe = subscribe((msg: AgentForgeWSMessage) => {
      const payload = msg.payload ?? {};
      // Ignore frames meant for a different session.
      if (payload.session_id !== sessionId) return;
      handlers[msg.type]?.(payload);
    });

    return unsubscribe;
  }, [
    sessionId,
    subscribe,
    appendStreamDelta,
    pushSubStep,
    patchSubStep,
    pushOutputBlock,
    patchBlockChips,
    clearStreamingDraft,
    commitStreamingDraft,
    setTabStatus,
    queryClient,
  ]);

  // Switching sessions mid-stream shouldn't leak the previous session's
  // accumulator into the new view — wipe the draft on session change.
  useEffect(() => clearStreamingDraft, [sessionId, clearStreamingDraft]);

  return { connected };
};
